<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
	<div
		:id="blok._uid"
		ref="wrapper"
		class="relative w-[inherit] space-y-2xl md:mx-md 2xl:mx-0"
	>
		<v-product-recommendations
			v-slot="{isLoading, products}"
			:name="blok.nosto_id"
			class="space-y-lg"
		>
			<div
				v-if="
					(blok.title && blok.title.length > 0) ||
					(blok.ingress && blok.ingress.length > 0)
				"
				class="space-y-lg"
				:class="{
					'mx-auto px-sm text-center': blok.titles_align !== 'left',
				}"
			>
				<v-h2 v-if="blok.title && blok.title.length > 0">
					{{ addHyphens(blok.title) }}
				</v-h2>

				<p
					v-if="blok.ingress && blok.ingress.length > 0"
					:class="{
						'md:mx-[20%]': blok.titles_align !== 'left',
					}"
				>
					{{ blok.ingress }}
				</p>
			</div>

			<!-- Avoid rendering on the server-side, since just rendering a
            scrollable with a bunch of functional product cards can apparently
            take a lot of time (especially because of the icon components they
            use), and this component is never the central content of the page,
            nor is it essential for SSR. Also delay rendering until in
            viewport, because it takes a long while client-side too and ruins
            lighthouse scores. -->
			<!-- Note that this is ugly copy-paste of the `v-scrollable`
            internal styles to make this look similar -->
			<div
				v-if="isLoading || !mounted || !hasBeenInView"
				class="relative scrollbar-thin flex snap-x scroll-px-sm scrollbar-thumb-black scrollbar-track-gray-100 flex-nowrap space-x-xs overflow-auto scroll-smooth px-sm scrollbar-thumb-rounded-full scrollbar-track-rounded-full md:scroll-px-0 md:space-x-sm md:px-0"
			>
				<div v-for="i in 10" :key="i" class="w-40 shrink-0 snap-start md:w-65">
					<v-product-card-placeholder />
				</div>
			</div>

			<v-scrollable
				v-else
				class="flex snap-x scroll-px-sm flex-nowrap space-x-xs px-sm md:scroll-px-0 md:space-x-sm md:px-0"
			>
				<v-scrollable-item
					v-for="product in products"
					:key="product.key"
					class="w-40 shrink-0 snap-start md:w-65"
				>
					<v-elevate-listing-product-card :product="product" />
				</v-scrollable-item>
			</v-scrollable>
		</v-product-recommendations>

		<div v-if="blok.button_link && blok.button_text" class="text-center">
			<v-button
				theme="black"
				size="lg"
				:to="url"
				:aria-label="blok.button_text"
				class="p-sm text-base font-normal uppercase"
			>
				{{ blok.button_text }}
			</v-button>
		</div>
	</div>
</template>

<script setup lang="ts">
import {useIntersectionObserver} from "@vueuse/core";
import type {BlokComponent} from "@pkg/shared/types";

import {useHyphenation} from "@pkg/shared/composables/use-hyphenation";
import formatSbUrl from "@pkg/shared/utils/format-sb-url";

const properties = defineProps<{
	blok: Extract<BlokComponent, {component: "sb-nosto"}>;
}>();

const wrapper = useTemplateRef("wrapper");

// Rendering a bunch of elevate-listing-product-cards has terrible performance on vue for some reason - vue spends like 2 seconds rendering the front page's product recommendations after page load. There's no singular part of the product card component that causes a huge spike in rendering time, nor are the v-scrollable items the cause of this. v-icon components inside the product cards take some time, but even if you remove them, the performance of rendering product cards can still be bad. So, we avoid rendering the whole thing until the user has this component in their viewport.
const hasBeenInView = ref(false);
const {stop: stopObserving} = useIntersectionObserver(wrapper, ([entry]) => {
	if (!entry?.isIntersecting) {
		return;
	}

	hasBeenInView.value = true;
	stopObserving();
});

const {hyphenate: addHyphens} = useHyphenation();

const mounted = ref(false);

const url = computed(() => formatSbUrl(properties.blok.button_link));

onMounted(() => {
	mounted.value = true;
});
</script>
